import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Medication } from 'fhir/r4';
import { take } from 'rxjs';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { ErrorService } from '../../shared/error.service';
import { AuthService } from '../../shared/auth/auth.service';

type DialogData = { resource: Medication };

@Component({
    selector: 'app-medication-stock-adjust-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatSlideToggleModule,
    ],
    template: `
    <h2 mat-dialog-title>Adjust Stock</h2>

    <div mat-dialog-content>
      <div class="substance-name">
        <strong>{{ substanceName }}</strong>
      </div>

      <form [formGroup]="form" class="form-stack">
                <mat-form-field appearance="outline">
          <mat-label>Total Remaining (dispensable units)</mat-label>
          <input matInput type="number" formControlName="quantityRemaining" min="0" />
          @if (unitLabel) {
            <mat-hint>Unit: {{ unitLabel }}</mat-hint>
          }
          <mat-error *ngIf="form.controls.quantityRemaining.hasError('required')">
            A quantity is required
          </mat-error>
          <mat-error *ngIf="form.controls.quantityRemaining.hasError('min')">
            Quantity cannot be negative
          </mat-error>
        </mat-form-field>

                <mat-form-field appearance="outline">
                    <mat-label>Low-stock Threshold</mat-label>
                    <input matInput type="number" formControlName="lowStockThreshold" min="0" [disabled]="!canEditLowStockConfig" />
                    <mat-hint>Alert when remaining units fall to or below this value.</mat-hint>
                </mat-form-field>

                <mat-slide-toggle color="primary" formControlName="lowStockAlertsEnabled" [disabled]="!canEditLowStockConfig">
                    Send low-stock alerts
                </mat-slide-toggle>
      </form>
    </div>

    <div mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
      <button mat-raised-button color="primary" type="button" (click)="save()" [disabled]="form.invalid || saving">
        <mat-icon>save</mat-icon>
        Save
      </button>
    </div>
  `,
    styles: [
        `
    .form-stack {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 12px;
    }

    .substance-name {
      margin-bottom: 8px;
    }
    `,
    ],
})
export class MedicationStockAdjustDialogComponent {
    private http = inject(HttpClient);
    private backendUrl = inject(backendEndPointToken);
    private terminologyServiceBaseUrl = inject(terminologyCanonicalUrlBaseToken);
    private fb = inject(FormBuilder);
    private errorService = inject(ErrorService);
    private dialogRef = inject(MatDialogRef<MedicationStockAdjustDialogComponent>);
    private data = inject(MAT_DIALOG_DATA) as DialogData;
    private authService = inject(AuthService);

    saving = false;

    private readonly extUrls = {
        totalRemaining: `${this.terminologyServiceBaseUrl}/StructureDefinition/totalRemaining`,
        modifiedHistory: `${this.terminologyServiceBaseUrl}/StructureDefinition/modifiedHistory`,
        totalRemainingChanges: `${this.terminologyServiceBaseUrl}/StructureDefinition/totalRemainingChanges`,
        dispensableUnits: `${this.terminologyServiceBaseUrl}/StructureDefinition/dispensableUnits`,
        lowStockThreshold: `${this.terminologyServiceBaseUrl}/StructureDefinition/lowStockThreshold`,
        lowStockAlertsEnabled: `${this.terminologyServiceBaseUrl}/StructureDefinition/lowStockAlertsEnabled`,
    } as const;

    readonly substanceName =
        this.data.resource?.code?.text ||
        this.data.resource?.code?.coding?.[0]?.display ||
        this.data.resource?.code?.coding?.[0]?.code ||
        this.data.resource?.id ||
        'Medication';

    readonly unitLabel = this.extractDispensableUnit(this.data.resource);
    private readonly initialQuantity = this.extractTotalRemaining(this.data.resource);
    private readonly initialLowStockThreshold = this.extractLowStockThreshold(this.data.resource);
    private readonly initialLowStockAlertsEnabled = this.extractLowStockAlertsEnabled(this.data.resource);
    readonly canEditLowStockConfig = this.authService.can('medication', 'stock');

    form = this.fb.group({
        quantityRemaining: [this.initialQuantity ?? 0, [Validators.required, Validators.min(0)]],
        lowStockThreshold: [{ value: this.initialLowStockThreshold ?? null, disabled: !this.canEditLowStockConfig }, [Validators.min(0)]],
        lowStockAlertsEnabled: [{ value: this.initialLowStockAlertsEnabled ?? false, disabled: !this.canEditLowStockConfig }],
    });

    close(): void {
        this.dialogRef.close();
    }

    save(): void {
        const id = (this.data.resource?.id ?? '').toString().trim();
        if (!id) {
            this.errorService.openandCloseError('Cannot update medication: missing id');
            return;
        }

        const formValue = this.form.getRawValue();
        const qtyRaw = formValue.quantityRemaining;
        const qty = qtyRaw === null || qtyRaw === undefined ? undefined : Number(qtyRaw);
        if (qty === undefined || Number.isNaN(qty) || qty < 0) {
            this.errorService.openandCloseError('Enter a valid non-negative quantity');
            return;
        }

        const next = JSON.parse(JSON.stringify(this.data.resource)) as Medication;
        next.extension = Array.isArray(next.extension) ? [...next.extension] : [];

        const beforeQty = this.initialQuantity === null ? undefined : this.initialQuantity;
        const afterQty = qty;

        this.upsertTotalRemaining(next, afterQty, this.unitLabel);
        const normalizedThreshold = this.normalizeThreshold(formValue.lowStockThreshold);
        this.upsertLowStockThreshold(next, normalizedThreshold, this.unitLabel);
        const normalizedAlertsFlag = this.normalizeBoolean(formValue.lowStockAlertsEnabled);
        this.upsertLowStockAlertsEnabled(next, normalizedAlertsFlag);

        if (beforeQty !== afterQty) {
            const changedAt = new Date().toISOString();
            this.appendModifiedHistory(next, {
                field: 'totalRemaining',
                beforeValue: beforeQty,
                editedValue: afterQty,
                unit: this.unitLabel,
                changedAt,
            });
            this.appendTotalRemainingChangeEntry(next, {
                beforeValue: beforeQty,
                editedValue: afterQty,
                unit: this.unitLabel,
                changedAt,
            });
        }

        this.saving = true;

        this.http
            .put<Medication>(`${this.backendUrl}/Medication/${encodeURIComponent(id)}`, next, {
                headers: {
                    'Content-Type': 'application/fhir+json',
                    Accept: 'application/fhir+json',
                    Prefer: 'return=representation',
                },
            })
            .pipe(take(1))
            .subscribe({
                next: (updated) => {
                    this.saving = false;
                    this.dialogRef.close(updated);
                },
                error: (err) => {
                    console.error('Failed to update medication stock', err);
                    this.saving = false;
                    this.errorService.openandCloseError('Failed to update medication stock. Please try again.');
                },
            });
    }

    private extractTotalRemaining(resource: Medication | null | undefined): number | null {
        const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.totalRemaining || e?.url?.includes('/StructureDefinition/totalRemaining'));
        const value = ext?.valueQuantity?.value ?? ext?.valueInteger ?? null;
        const n = value === null || value === undefined ? NaN : Number(value);
        return Number.isFinite(n) ? n : null;
    }

    private extractDispensableUnit(resource: Medication | null | undefined): string | undefined {
        const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.dispensableUnits || e?.url?.includes('/StructureDefinition/dispensableUnits'));
        const medAny = resource as Record<string, any> | null | undefined;
        const unit =
            (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.totalRemaining)?.valueQuantity?.unit ||
            ext?.valueString ||
            ext?.valueCodeableConcept?.text ||
            ext?.valueCodeableConcept?.coding?.[0]?.display ||
            medAny?.['suppliedItem']?.quantity?.unit;
        const trimmed = typeof unit === 'string' ? unit.trim() : '';
        return trimmed || undefined;
    }

    private extractLowStockThreshold(resource: Medication | null | undefined): number | null {
        const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.lowStockThreshold);
        if (!ext) {
            return null;
        }
        const raw = ext.valueQuantity?.value ?? ext.valueInteger ?? null;
        const numeric = raw === null || raw === undefined ? NaN : Number(raw);
        return Number.isFinite(numeric) ? numeric : null;
    }

    private extractLowStockAlertsEnabled(resource: Medication | null | undefined): boolean | null {
        const ext = (resource?.extension ?? []).find((e: any) => e?.url === this.extUrls.lowStockAlertsEnabled);
        if (ext?.valueBoolean === true || ext?.valueBoolean === false) {
            return ext.valueBoolean;
        }
        return null;
    }

    private normalizeThreshold(value: any): number | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const numeric = Number(value);
        if (!Number.isFinite(numeric) || numeric < 0) {
            return null;
        }
        return numeric;
    }

    private normalizeBoolean(value: any): boolean | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        if (typeof value === 'boolean') {
            return value;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (!normalized) return null;
            if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
            if (['false', '0', 'no', 'off'].includes(normalized)) return false;
        }
        if (typeof value === 'number') {
            if (value === 1) return true;
            if (value === 0) return false;
        }
        return null;
    }

    private upsertTotalRemaining(resource: Medication, value: number, unit?: string) {
        resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
        const extensions = resource.extension as any[];
        const targetUrl = this.extUrls.totalRemaining;
        const idx = extensions.findIndex((e: any) => e?.url === targetUrl);
        const payload: any = {
            url: targetUrl,
            valueQuantity: {
                value,
                ...(unit ? { unit } : {}),
            },
        };

        if (idx >= 0) {
            extensions[idx] = payload;
        } else {
            extensions.push(payload);
        }
    }

    private upsertLowStockThreshold(resource: Medication, value: number | null, unit?: string) {
        resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
        const extensions = resource.extension as any[];
        const targetUrl = this.extUrls.lowStockThreshold;
        const idx = extensions.findIndex((e: any) => e?.url === targetUrl);

        if (value === null) {
            if (idx >= 0) {
                extensions.splice(idx, 1);
            }
            return;
        }

        const payload: any = {
            url: targetUrl,
            valueQuantity: {
                value,
                ...(unit ? { unit } : {}),
            },
        };

        if (idx >= 0) {
            extensions[idx] = payload;
        } else {
            extensions.push(payload);
        }
    }

    private upsertLowStockAlertsEnabled(resource: Medication, value: boolean | null) {
        resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
        const extensions = resource.extension as any[];
        const targetUrl = this.extUrls.lowStockAlertsEnabled;
        const idx = extensions.findIndex((e: any) => e?.url === targetUrl);

        if (value === null) {
            if (idx >= 0) {
                extensions.splice(idx, 1);
            }
            return;
        }

        const payload: any = {
            url: targetUrl,
            valueBoolean: value,
        };

        if (idx >= 0) {
            extensions[idx] = payload;
        } else {
            extensions.push(payload);
        }
    }

    private appendModifiedHistory(
        resource: Medication,
        entry: {
            field: string;
            beforeValue?: number;
            editedValue?: number;
            unit?: string;
            changedAt: string;
        }
    ) {
        resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
        const extensions = resource.extension as any[];
        const history = {
            url: this.extUrls.modifiedHistory,
            extension: [
                { url: 'field', valueString: entry.field },
                ...(entry.beforeValue !== undefined
                    ? [
                        {
                            url: 'beforeValue',
                            valueQuantity: {
                                value: entry.beforeValue,
                                ...(entry.unit ? { unit: entry.unit } : {}),
                            },
                        },
                    ]
                    : []),
                ...(entry.editedValue !== undefined
                    ? [
                        {
                            url: 'editedValue',
                            valueQuantity: {
                                value: entry.editedValue,
                                ...(entry.unit ? { unit: entry.unit } : {}),
                            },
                        },
                    ]
                    : []),
                { url: 'changedAt', valueDateTime: entry.changedAt },
            ],
        };

        extensions.push(history);
    }

    private appendTotalRemainingChangeEntry(
        resource: Medication,
        entry: {
            beforeValue?: number;
            editedValue?: number;
            unit?: string;
            changedAt: string;
        }
    ) {
        if (entry.beforeValue === undefined && entry.editedValue === undefined) return;
        resource.extension = Array.isArray(resource.extension) ? resource.extension : [];
        const extensions = resource.extension as any[];

        const currentUser = this.authService.user.getValue();
        const practitionerReference = currentUser?.fhirResourceId
            ? {
                reference: `Practitioner/${currentUser.fhirResourceId}`,
                display: currentUser?.display || currentUser?.name || currentUser?.email,
            }
            : undefined;
        const practitionerDisplay = currentUser?.display || currentUser?.name || currentUser?.email;

        const children: any[] = [
            { url: 'dateChanged', valueDateTime: entry.changedAt },
            practitionerReference
                ? { url: 'practitionerWhoChanged', valueReference: practitionerReference }
                : practitionerDisplay
                    ? { url: 'practitionerWhoChanged', valueString: practitionerDisplay }
                    : null,
            ...(entry.beforeValue !== undefined
                ? [
                    {
                        url: 'totalRemainingBeforeChange',
                        valueQuantity: {
                            value: entry.beforeValue,
                            ...(entry.unit ? { unit: entry.unit } : {}),
                        },
                    },
                ]
                : []),
            ...(entry.editedValue !== undefined
                ? [
                    {
                        url: 'totalRemainingAfterChange',
                        valueQuantity: {
                            value: entry.editedValue,
                            ...(entry.unit ? { unit: entry.unit } : {}),
                        },
                    },
                ]
                : []),
        ].filter(Boolean);

        extensions.push({
            url: this.extUrls.totalRemainingChanges,
            extension: children,
        });
    }
}
